<template>
  <n-space
    align="center"
    justify="space-between"
    class="connection-bar"
    :size="[16, 32]"
    :wrap-item="false"
  >
    <n-space
      align="center"
      class="connection-bar__info"
      :wrap-item="false"
    >
      <n-h3 class="connection-bar__title">
        {{
          connected
            ? t('common.connection.fitbike')
            : t('common.connection.connectFitbike')
        }}
      </n-h3>
    </n-space>

    <n-space
      v-if="connected"
      align="center"
      :size="16"
    >
      <div class="connection-bar__status">
        <span>{{ t('common.connection.connected') }}</span>
      </div>
    </n-space>

    <n-space class="connection-bar__buttons">
      <BfButton
        v-if="!connected && !showModal"
        :state="connecting ? ButtonState.Loading : ButtonState.Enabled"
        @click="connect"
      >
        {{
          connected
            ? t('common.connection.connected')
            : t('common.connection.connect')
        }}
      </BfButton>
      <BfButton
        v-if="connected"
        :type="ButtonType.Outlined"
        @click="disconnect"
      >
        {{ t('common.connection.disconnect') }}
      </BfButton>
    </n-space>
  </n-space>

  <BikeStatus v-if="connected" />

  <n-modal
    v-model:show="showModal"
    style="width: 600px"
    class="custom-card"
    preset="card"
    :title="t('common.connection.failed.title')"
    :bordered="false"
    size="huge"
  >
    <n-form
      v-if="!connected"
      ref="formRef"
      label-placement="top"
    >
      <n-form-item :label="t('common.connection.protocolHostPort')">
        <n-input-group
          class="connection-bar__address"
          @keyup.enter="createConnection"
        >
          <n-select
            v-model:value="connection.protocol"
            :options="options"
            :style="{ width: '20%' }"
            :disabled="connecting"
            @update:value="handleProtocolChange"
          />

          <n-input
            v-model:value="connection.host"
            :style="{ width: '60%' }"
            :disabled="connecting"
          />

          <n-input-number
            v-model:value="connection.port"
            :style="{ width: '20%' }"
            :show-button="false"
            :disabled="connecting"
          />
        </n-input-group>
        <BfButton
          v-if="!connected"
          :state="connecting ? ButtonState.Loading : ButtonState.Enabled"
          style="margin-left: 12px"
          @click="connect"
        >
          {{
            connected
              ? t('common.connection.connected')
              : t('common.connection.connect')
          }}
        </BfButton>
      </n-form-item>

      <n-form-item
        :label="t('common.connection.discover.info')"
        class="connection-bar__discover"
      >
        <n-input-group
          class="connection-bar__address"
          @keyup.enter="discover"
        >
          <n-input
            v-model:value="macAddress"
            :placeholder="t('common.connection.discover.macaddress')"
            :disabled="connecting"
          />
        </n-input-group>
        <BfButton
          v-if="!connected"
          :state="connecting ? ButtonState.Disabled : ButtonState.Enabled"
          :type="ButtonType.Outlined"
          style="margin-left: 12px"
          @click="discover"
        >
          {{ t('common.connection.discover.title') }}
        </BfButton>
      </n-form-item>
    </n-form>
    <div class="connection-bar__hint">
      <p>{{ t('common.connection.failed.content') }}</p>
      <ul>
        <li>{{ t('common.connection.failed.tip1') }}</li>
        <li>{{ t('common.connection.failed.tip2') }}</li>
        <li>{{ t('common.connection.failed.tip3') }}</li>
      </ul>
    </div>
  </n-modal>
</template>

<script setup lang="ts">
import {
  NInput,
  NInputGroup,
  NSelect,
  NForm,
  NFormItem,
  NInputNumber,
  NSpace,
  NH3,
  NModal,
  useMessage,
} from 'naive-ui'

import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useMqttStore } from '@/store/mqttStore'
import { usePositionStore } from '@/store/positionStore'
import { storeToRefs } from 'pinia'
import { BfButton, ButtonType, ButtonState } from '@finetic/bikefitting-ui'
import BikeStatus from '@/components/controls/BikeStatus.vue'

const router = useRouter()
const mqttStore = useMqttStore()
const positionStore = usePositionStore()
const message = useMessage()
const { t } = useI18n()

const {
  createConnection,
  destroyConnection,
  handleProtocolChange,
  discoverIPAddress,
} = mqttStore
const { resetPosition, startHoming } = positionStore
const { client, connection, connected, connecting, connectionError } =
  storeToRefs(mqttStore)

const macAddress = ref('')

const options = [
  {
    label: 'ws://',
    value: 'ws',
  },
  {
    label: 'wss://',
    value: 'wss',
  },
]

const showModal = computed({
  get() {
    return !connected.value && connectionError.value ? true : false
  },
  set(newValue) {
    if (newValue == false) {
      connectionError.value = ''
    }
  },
})

const connect = () => {
  createConnection()
  startHoming()
  resetPosition()

  router.push({ name: 'start' })
}

const discover = () => {
  discoverIPAddress(macAddress.value).then((data) => {
    if (data) {
      message.success(t('common.connection.discover.found'))
    } else {
      message.warning(t('common.connection.discover.noresult'))
    }
  })
}

const disconnect = () => {
  destroyConnection(client.value)

  router.push({ name: 'start' })
}

connect()
</script>

<style lang="scss" scoped>
.connection-bar {
  padding: 10px 32px;
  background-color: #1a1d1f;
  border-radius: 0.8rem;
  height: 64px;

  &__title {
    margin-bottom: 0;
  }

  &__address {
    :deep(.n-input-number .n-input) {
      --n-color: rgba(255, 255, 255, 0.1) !important;
    }
  }

  &__discover {
    margin-top: 24px;
  }

  &__hint {
    font-size: 14px;
    margin-top: 24px;
    color: rgb(255, 255, 255, 0.6);
  }

  :deep(.n-form-item) {
    .n-form-item-label {
      max-width: none;
    }

    .n-form-item-feedback-wrapper {
      display: none;
    }
  }

  &__status {
    &::before {
      content: '';
      display: inline-block;
      margin-right: 12px;
      height: 10px;
      width: 10px;
      background: #00ff57;
      box-shadow:
        0 0 4px #00ff57,
        0 0 10px #00ff57;
      border-radius: 50%;
    }

    color: #00ff57;
  }

  &__status-text {
    color: #00ff57;
    font-weight: 500;
  }

  @media only screen and (max-width: 700px) {
    padding: 20px 32px;
  }

  @media only screen and (max-width: 500px) {
    flex-direction: column !important;
    align-items: stretch !important;

    :deep(.n-space.connection-bar__buttons) {
      flex-direction: column !important;
      align-items: stretch !important;
    }

    .n-form-item.n-form-item--left-labelled {
      grid-template-areas:
        'label label'
        'blank blank';

      justify-items: start;
    }

    .display-mobile-none {
      display: none;
    }
  }
}
</style>
